// src/services/patientMedicineInventoryService.js
import apiClient from "./apiClient";

// Patient Medicine Inventory Service
// All operations use apiClient which automatically adds organizationId as query parameter
// This ensures all operations use organization-specific tables:
// - {orgId}_patient_medicine_inventory
// - {orgId}_medicine_administration_history
// Medicine ID references come from base medicine_raw_details table (reference data only)
export const patientMedicineInventoryService = {
  // Get patient medicine inventory by admission ID
  // Uses organization-specific {orgId}_patient_medicine_inventory table
  getByAdmissionId: async (admissionId) => {
    try {
      // apiClient will automatically add organizationId as query param via interceptor
      const response = await apiClient.get(`/patient-medicine-inventory/admission/${admissionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching patient medicine inventory:', error);
      throw error;
    }
  },

  // Add medicine to patient inventory (when issued from pharmacy)
  // Saves to organization-specific {orgId}_patient_medicine_inventory table
  addToPatientInventory: async (inventoryData) => {
    try {
      // apiClient will automatically add organizationId as query param via interceptor
      // Backend will save to {orgId}_patient_medicine_inventory table
      const response = await apiClient.post(`/patient-medicine-inventory/add`, inventoryData);
      return response.data;
    } catch (error) {
      console.error('Error adding medicine to patient inventory:', error);
      throw error;
    }
  },

  // Administer medicine to patient (reduce patient inventory)
  // Updates {orgId}_patient_medicine_inventory and creates record in {orgId}_medicine_administration_history
  administerToPatient: async (administrationData) => {
    try {
      // apiClient will automatically add organizationId as query param via interceptor
      // Backend will:
      // 1. Update {orgId}_patient_medicine_inventory (reduce quantity)
      // 2. Create record in {orgId}_medicine_administration_history
      const response = await apiClient.post(`/patient-medicine-inventory/administer`, administrationData);
      return response.data;
    } catch (error) {
      console.error('Error administering medicine to patient:', error);
      throw error;
    }
  },

  // Return medicine from patient inventory to pharmacy
  // Updates {orgId}_patient_medicine_inventory and {orgId}_medicine_inventory (adds quantity back)
  // Creates transaction in {orgId}_medicine_transactions
  returnToPharmacy: async (returnData) => {
    try {
      // apiClient will automatically add organizationId as query param via interceptor
      // Backend will:
      // 1. Update {orgId}_patient_medicine_inventory (reduce quantity)
      // 2. Update {orgId}_medicine_inventory (add quantity back)
      // 3. Create transaction in {orgId}_medicine_transactions
      const response = await apiClient.post(`/patient-medicine-inventory/return`, returnData);
      return response.data;
    } catch (error) {
      console.error('Error returning medicine to pharmacy:', error);
      throw error;
    }
  },

  // Get medicine administration history
  // Fetches from organization-specific {orgId}_medicine_administration_history table
  getAdministrationHistory: async (admissionId) => {
    try {
      // apiClient will automatically add organizationId as query param via interceptor
      const response = await apiClient.get(`/patient-medicine-inventory/administration-history/${admissionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching administration history:', error);
      throw error;
    }
  },

  // Update patient inventory quantity
  // Updates organization-specific {orgId}_patient_medicine_inventory table
  updateQuantity: async (inventoryId, quantityData) => {
    try {
      // apiClient will automatically add organizationId as query param via interceptor
      const response = await apiClient.put(`/patient-medicine-inventory/${inventoryId}`, quantityData);
      return response.data;
    } catch (error) {
      console.error('Error updating patient inventory quantity:', error);
      throw error;
    }
  },
};

// Export individual functions for convenience
export const {
  getByAdmissionId,
  addToPatientInventory,
  administerToPatient,
  returnToPharmacy,
  getAdministrationHistory,
  updateQuantity,
} = patientMedicineInventoryService;
